/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import React, { FunctionComponent } from 'react';

import PropTypes from 'prop-types';

import SelectMark from '../../../assets/images/hydra_icon_status_checkmark.png';
import { Mousetrap } from '../../common';
import { MenuProps } from './MenuProps';

import './Menu.scss';

const Menu: FunctionComponent<MenuProps> = (props): JSX.Element => {
    const { items, selectedIndex, isFocused, tick, name, focusedIndex } = props;
    const isSimpleList = items?.length && typeof items[0] === 'string';

    const styleItem = (
        index: number,
        isItemSelected: boolean,
        isDisabled: boolean,
        isTruncated: boolean,
    ) => {
        let menuItemClass = tick === undefined ? 'menu-item' : 'menu-item menu-item-with-tick';
        menuItemClass = isItemSelected ? `${menuItemClass} item-selected` : menuItemClass;

        if (index === selectedIndex) {
            menuItemClass = `${menuItemClass} item-muted`;
        }

        if (index === focusedIndex) {
            menuItemClass = isFocused ? `${menuItemClass} item-muted item-focus` : menuItemClass;
        }

        if (isDisabled) {
            menuItemClass = `${menuItemClass} item-disabled`;
        }

        if (isTruncated) {
            menuItemClass = `${menuItemClass} ${index === focusedIndex ? 'highlight-truncate' : 'truncate'} `;
        }

        return menuItemClass;
    };

    const simpleListItem = (isItemSelected: boolean, title: string) => {
        return (
            <>
                {isItemSelected && (
                    <span className="select-mark-span">
                        <img src={SelectMark} className="select-mark" />
                    </span>
                )}
                <span className="item-title">{title}</span>
            </>
        );
    };

    const multiValuedListItem = (title: string, value: string) => {
        const titleElement = <span className="item-title">{title}</span>;
        const valueElement = <span className="item-value">{value}</span>;

        return (
            <>
                {titleElement}
                {value ? valueElement : ''}
            </>
        );
    };

    return (
        <div className="menu-container">
            {items.map((item, index) => {
                const isItemSelected = tick === index;
                const itemClass = styleItem(
                    index,
                    isItemSelected,
                    item.isDisabled,
                    item.isTruncated,
                );

                return (
                    <Mousetrap key={index} value={`${index}-${name}`}>
                        <div key={index} className={itemClass}>
                            {isSimpleList
                                ? simpleListItem(isItemSelected, item)
                                : multiValuedListItem(item.title, item.value)}
                        </div>
                    </Mousetrap>
                );
            })}
        </div>
    );
};

Menu.propTypes = {
    isFocused: PropTypes.bool,
    selectedIndex: PropTypes.any,
    items: PropTypes.any,
    tick: PropTypes.number,
    name: PropTypes.any,
    focusedIndex: PropTypes.any,
};

export default Menu;
